import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { ChevronLeft, ChevronRight, Search, Building2, UserCog } from "lucide-react";
import { Input } from "@/components/ui/Input";
import api from "../../lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";

const StackPreportPage = () => {
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const queryClient = useQueryClient();

  const formatLabel = (value) => {
    if (!value) return "-";
    const text = String(value).replace(/_/g, " ").toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const formatReviewerDisplay = (row) => {
    const fullName = [row.assigned_reviewer_first_name, row.assigned_reviewer_last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    return {
      name: fullName || row.assigned_reviewer_email || "-",
      email: row.assigned_reviewer_email || null,
    };
  };

  // Fetch report data
  const { data: reportResponse, isLoading: isReportLoading } = useQuery(
    ["report", id],
    async () => {
      const response = await api.get(`/reports/${id}`);
      return response.data;
    },
    {
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );
  const report = reportResponse?.data || reportResponse;

  // Fetch stack entries
  const { data: stackResponse, isLoading: isStackLoading } = useQuery(
    ["report-stack", id, page, limit],
    async () => {
      const response = await api.get(`/reports/${id}/stack?page=${page}&limit=${limit}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  const pagination = stackResponse?.pagination;
  const stackRows = stackResponse?.data || [];

  const invalidateStack = () => {
    queryClient.invalidateQueries(["report-stack", id]);
  };

  const handleView = async (stackId) => {
    try {
      const res = await api.get(`/reports/${id}/stack/${stackId}/download?action=view`);
      const fileUrl = res.data?.url;
      const filename = res.data?.filename || "document";
      if (!fileUrl) return;
      const w = window.open(fileUrl, "_blank");
      if (w && filename) {
        try {
          w.document.title = filename;
        } catch (e) { }
      }
      invalidateStack();
    } catch (err) {
      console.error("View stack file error", err);
    }
  };

  if (isReportLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="w-full ">
        {/* Main Content */}
        <div className="slide-up">
          <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2">
            <div className="space-y-3">
              <Label className="pl-4 text-xs font-medium uppercase text-gray-0">
                Report Name:
              </Label>
              <div className="w-full h-auto px-4 py-3 text-sm truncate rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                {report?.name || "N/A"}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="pl-4 text-xs font-medium uppercase text-gray-0">
                Visit Date:
              </Label>
              <div className="w-full h-auto px-4 py-3 text-sm truncate rounded-t-lg bg-navy-100 text-gray-0 whitespace-nowrap">
                {report?.visited_date
                  ? new Date(report.visited_date).toLocaleDateString()
                  : "N/A"}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <UserCog className="w-5 text-red-500" />
                <p className="text-sm text-gray-400">This icon indicates that the record has been added or updated by the admin or the system</p>
              </div>
            </div>
          </div>


          <Table>
            <TableHeader>
              <TableRow className="border-none">
                <TableHead className="h-10">Stage</TableHead>
                <TableHead className="h-10">Status</TableHead>
                <TableHead className="h-10">User</TableHead>
                <TableHead className="h-10 w-0 whitespace-nowrap">Date</TableHead>
                <TableHead className="h-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isStackLoading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <div className="py-8 text-center text-gray-400">Loading stack...</div>
                  </TableCell>
                </TableRow>
              ) : stackRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center">
                    <div className="text-gray-500">
                      <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Right now not have any reports stack</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                stackRows.map((row, index) => {
                  const reviewer = formatReviewerDisplay(row);

                  return (
                    <TableRow key={`${row.id}-${index}`}>
                      <TableCell>
                        <div className="flex justify-between p-4 m-2 rounded-t-lg bg-navy-100 text-gray-0">
                          <div className="flex items-center gap-3 truncate whitespace-nowrap ">{formatLabel(row.stage)}</div>
                          {(row.added_by_is_admin || row.updated_by_is_admin) && (
                            <div><UserCog className="w-5 text-red-500" /></div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-between p-4 m-2 rounded-t-lg bg-navy-100 text-gray-0">
                          <div className="flex items-center gap-3 truncate whitespace-nowrap">{formatLabel(row.status)}</div>
                          {(row.added_by_is_admin || row.updated_by_is_admin) && (
                            <div><UserCog className="w-5 text-red-500" /></div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-between p-4 m-2 rounded-t-lg bg-navy-100 text-gray-0">
                          <div className="flex flex-row min-w-0 gap-1 items-center">
                            <span className="font-medium truncate whitespace-nowrap">{reviewer.name}</span>
                            {reviewer.email && (
                              <span className="text-xs truncate text-gray-400">{reviewer.email}</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="w-0 whitespace-nowrap">
                        <div className="w-fit p-4 m-2 rounded-t-lg bg-navy-100 text-gray-0">
                          {row.date ? new Date(row.date).toLocaleString() : "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="p-2 m-2 text-center rounded-t-lg bg-navy-100 text-gray-0">
                          {row.file_path ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="p-0 truncate whitespace-nowrap text-gray-0 hover:text-red-600"
                              onClick={() => handleView(row.id)}
                            >
                              View
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" className="p-0 truncate text-gray-0 whitespace-nowrap" disabled>
                              No file
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pagination && (
            <div className="flex flex-col pr-0 border-t border-b border-gray-0 sm:flex-row sm:items-center sm:justify-between sm:pr-12">
              <div className="py-4 text-sm text-center text-gray-400 uppercase sm:text-left">
                Showing {pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1}{" "}
                to{" "}
                {pagination.total === 0
                  ? 0
                  : (pagination.page - 1) * pagination.limit + (stackRows?.length || 0)}{" "}
                of {pagination.total} records
              </div>
              <div className="flex items-center justify-center w-auto">
                <Button
                  variant="outline"
                  className="flex items-center justify-center h-auto p-5 text-xs font-normal uppercase border-r rounded-none rounded-b-none border-gray-0 text-gray-0 hover:scale-100"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                  PREVIOUS
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center h-auto p-5 text-xs font-normal uppercase border-r rounded-none rounded-b-none border-gray-0 text-gray-0 hover:scale-100"
                >
                  {page}
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center h-auto p-5 text-xs font-normal uppercase border-0 rounded-none rounded-b-none text-gray-0 hover:scale-100"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                >
                  NEXT
                  <ChevronRight className="w-4 h-4 ml-2 text-red-500" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default StackPreportPage;
